import { useEffect, useRef, useState } from "react";
import { Draggable, DropResult } from "react-beautiful-dnd";
import styled from "styled-components";
import {
    ALL_PREMIUM_SOUNDS,
    FREE_SOUNDS as ONLY_FREE_SOUNDS,
    SoundItem,
    SoundItemMap,
} from "@lifeat/shared/data-models/widget/Soundboard/SoundboardDataModels";
import LifeAtVolumeSlider from "@common/LifeAtVolumeSlider/LifeAtVolumeSlider";
import { useGlobalAudioContext } from "@contexts/GlobalAudioContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import Styles from "@themes/Styles";
import * as DragAndDropUtil from "@util/DragAndDropUtil";
import * as SoundboardWidgetService from "../../../service/SoundboardWidgetService";
import { saveSoundsOrderForUser } from "../../../service/SoundboardWidgetService";
import Soundboard from "./Soundboard";
import SoundboardSound from "./SoundboardSound";
import { SoundboardYoutubePlayer } from "./SoundboardYoutubePlayer";

const FREE_SOUNDS = [
    // Actual free sounds
    ...ONLY_FREE_SOUNDS,
    // Placeholder sounds to entice user to upgrade (NOTE: These should match the ones in SoundboardWidgetFunctions)
    ...ALL_PREMIUM_SOUNDS.map((premiumSound) => ({ ...premiumSound, src: "" })),
];

const MASTER_VOLUME_LABEL = "Master Volume";
const HEADER_LABEL = "Soundboard";
const YOUTUBE_LINK_LABEL = "Youtube Link";
const AMBIANCE_LABEL = "Ambiance";
const DEFAULT_YOUTUBE_URL = "https://youtu.be/jfKfPfyJRdk";

const Container = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? "block" : "none")};
    overflow: auto;
    align-items: center;
    height: 100%;
    border-radius: ${Styles.widgetBorderRadius};
    background-color: ${({ theme }) => theme.backgroundPrimary};

    & .lifeat-volume-slider {
        padding: 0;
        & input {
            margin-left: 0;
        }
    }

    & .volume-slider-toggle {
        padding-left: 0;
        & .volume-icon {
            transform: none;
        }
    }
`;

const SidebarHeader = styled.h1`
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 24px;
`;

const Section = styled.div`
    padding: 20px;
`;

const SectionHeader = styled.h2`
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 8px;
`;

const MasterVolumeSlider = styled(LifeAtVolumeSlider)`
    width: 75%;
`;

const SpaceVolumeSlider = styled(LifeAtVolumeSlider)`
    width: 75%;
    opacity: ${(props) => (props.disabled ? 0.6 : 1.0)};
`;

const Divider = styled.hr`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.borderPrimary};
`;

type SoundboardWidgetProps = {
    show: boolean;
};
export function SoundboardSidebar(props: SoundboardWidgetProps) {
    const { user, userData }: UserContextType = useUserContext();
    const { spaceController } = useVirtualSpaceContext();
    const { masterVolume, setMasterVolume } = useGlobalAudioContext();
    const isMuted = masterVolume === 0;

    const [allSounds, setAllSounds] = useState<{ [id: string]: SoundItem }>(undefined);

    // This hook handles setting data for initial component mount (i.e. with free data).
    useEffect(() => {
        const allSoundsTemp: { [id: string]: SoundItem } = {};
        for (const sound of FREE_SOUNDS) {
            allSoundsTemp[sound.soundId] = sound;
        }
        setAllSounds(allSoundsTemp);
    }, []);

    // This hook runs when the user's data is realized by Firebase and we can
    // read their data from the DB.
    const hasInitUserData = useRef(null);
    useEffect(() => {
        if (user && userData && !hasInitUserData.current) {
            if (userData?.proUserInfo?.isProUser) {
                // If user is premium, read the premium sounds from DB (protected by security rules)
                // and update our sounds data.
                const updateSoundsCb = (premiumSounds: { [id: string]: SoundItem }) => {
                    const allSoundsTemp: SoundItemMap = {};
                    // First include all free sounds
                    for (const sound of FREE_SOUNDS) {
                        allSoundsTemp[sound.soundId] = sound;
                    }
                    // Then add premium sounds
                    for (const sound of Object.values(premiumSounds)) {
                        allSoundsTemp[sound.soundId] = sound;
                    }
                    setAllSounds(allSoundsTemp);
                    hasInitUserData.current = true;
                };
                // Read the data from DB
                SoundboardWidgetService.fetchPremiumSounds(user.uid, updateSoundsCb);
            }
        }
    }, [user, userData]);

    const userSoundOrder = userData?.soundboardWidgetSettings?.savedSoundsOrder;
    const [soundOrder, setSoundOrder] = useState<string[]>(undefined);
    useEffect(() => {
        if (userSoundOrder && allSounds) {
            const updatedSoundOrder = [...userSoundOrder];
            for (const soundId of Object.keys(allSounds)) {
                if (!userSoundOrder.includes(soundId)) {
                    updatedSoundOrder.push(soundId);
                }
            }
            setSoundOrder(updatedSoundOrder);
        }
    }, [userSoundOrder, allSounds]);

    useEffect(() => {
        if (allSounds) {
            // If no sound order set yet, we default.
            if (!soundOrder) {
                setSoundOrder(Object.keys(allSounds));
            }
        }
    }, [allSounds]);

    function onDragEnd(result: DropResult): void {
        DragAndDropUtil.handleOnDragEnd(result, soundOrder, (updatedData: string[]) => {
            setSoundOrder(updatedData);
            saveSoundsOrderForUser(updatedData);
        });
    }

    /**
     * TODO
     * [X] Free sounds (served in code, so if we need to remove a sound it happens for everyone)
     * [X] AirTable button to ask users what other sounds they want and other feedback
     * [ ] Add support for handling sounds that we no longer support so saved configs don't break
     * [X] Premium sounds (data served from serverless function) OR stored in systemManagedData
     * [ ] Save soundboard config to user account data
     * [ ] Save multiple soundboard configs to user account (Premium)
     * [ ] Reorder Sounds (Volume Sliders) (require account)
     */

    return (
        <Container show={props.show}>
            <Section>
                <SidebarHeader>{HEADER_LABEL}</SidebarHeader>
                <SectionHeader>{MASTER_VOLUME_LABEL}</SectionHeader>
                <MasterVolumeSlider volume={masterVolume} setVolume={setMasterVolume} />
            </Section>
            <Divider />
            <Section>
                <SectionHeader>Spaces Audio</SectionHeader>
                <SpaceVolumeSlider
                    volume={spaceController.playerVolume}
                    setVolume={spaceController.setPlayerVolume}
                    disabled={isMuted}
                />
            </Section>
            <Divider />
            <Section>
                <SectionHeader>{YOUTUBE_LINK_LABEL}</SectionHeader>
                {userData && (
                    <SoundboardYoutubePlayer
                        initialUrl={userData.soundboardWidgetSettings?.youtubeUrl ?? DEFAULT_YOUTUBE_URL}
                        initialVolume={userData.soundboardWidgetSettings?.youtubeVolume ?? 0}
                    />
                )}
            </Section>
            <Divider />
            <Section>
                <SectionHeader>{AMBIANCE_LABEL}</SectionHeader>
                {soundOrder && allSounds && userData ? (
                    <Soundboard handleReorderSound={onDragEnd}>
                        {soundOrder.map((soundId: string, idx: number) => {
                            const sound: SoundItem = allSounds[soundId];

                            // Slider/Toggle should be disabled if the soundboard volume is off or if
                            // the sound is a premium sound and the user is a free user.
                            const isSoundDisabledPremium = sound.isPremium && !userData.proUserInfo?.isProUser;

                            const initialVolume = userData.soundboardWidgetSettings?.soundVolumes?.[soundId] ?? 0;

                            return (
                                <Draggable
                                    key={`draggable-task-${sound.soundId}`}
                                    draggableId={`draggable-task-${sound.soundId}`}
                                    index={idx}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <SoundboardSound
                                                soundInfo={sound}
                                                initialVolume={initialVolume}
                                                shouldDisablePremiumSounds={isSoundDisabledPremium}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            );
                        })}
                    </Soundboard>
                ) : null}
            </Section>
        </Container>
    );
}
